import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import ProductsService from "../../../api/services/products.service";
import { ProductDetailTypes } from "../../../api/types/products.types";
import styles from "./ProductDetail.module.scss";
import DeleteIcon from "../../../assets/images/actions/DeleteIcon";
import { toast } from "sonner";
import Loading from "../../../components/common/loading/Loading";

const ProductDetail = () => {
  const { t } = useTranslation("translation");
  const { id, product_id } = useParams();
  const [data, setData] = useState<Partial<ProductDetailTypes>>({});
  const getDetail = async () => {
    const res = await ProductsService.getSingleProduct(
      Number(id),
      Number(product_id),
    );
    setData(res.data);
  };

  useEffect(() => {
    const getDetail = async () => {
      const res = await ProductsService.getSingleProduct(
        Number(id),
        Number(product_id),
      );
      setData(res.data);
    };
    getDetail();
  }, [id, product_id]);

  const handleDeleteImage = async (imageId: number) => {
    try {
      const formData = new FormData();
      formData.append("delete_images", imageId?.toString());
      await ProductsService.deleteProductImages(
        Number(id),
        Number(product_id),
        formData,
      );

      getDetail();
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        (t("product_detail.error_unexpected") as string);
      toast.error(message);
    }
  };

  if (!data || Object.keys(data).length === 0) {
    return <Loading />;
  }

  return (
    <div className={styles.productDetail}>
      <div className={styles.productHeader}>
        <div className={styles.productMeta}>
          <span className={styles.sku}>
            {t("product_detail.sku_label") as string} {data.sku}
          </span>
        </div>
        <h1 className={styles.productTitle}>
          {data.name_en ||
            (t("product_detail.product_name_fallback") as string)}
        </h1>
      </div>
      {data.images && data.images.length > 0 && (
        <div className={styles.imagesGrid}>
          <h3>{t("product_detail.product_images") as string}</h3>
          <div className={styles.imagesContainer}>
            {data.images.map((image, index) => (
              <div key={image.id} className={styles.imageCard}>
                <img
                  src={image.image}
                  alt={`${data.name_en} ${index + 1}`}
                  className={styles.productImage}
                />
                <button
                  className={styles.deleteImage}
                  onClick={() => handleDeleteImage(image.id)}
                >
                  <DeleteIcon />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className={styles.infoSections}>
        <div className={styles.section}>
          <div className={styles.priceSection}>
            {data.discount_percent && data.discount_percent > 0 ? (
              <div>
                <p className={styles.discount_price}>
                  {data.price?.toFixed(2)} {data.product?.currency?.name}
                </p>
                <h2 className={styles.price}>
                  {data.discount_price?.toFixed(2)}{" "}
                  {data.product?.currency?.name}
                </h2>
              </div>
            ) : (
              <h2 className={styles.price}>
                {data.price?.toFixed(2)} {data.product?.currency?.name}
              </h2>
            )}
            <div className={styles.stock}>
              <span
                className={`${styles.stockStatus} ${
                  data.stock_quantity && data.stock_quantity > 0
                    ? styles.inStock
                    : styles.outOfStock
                }`}
              >
                {data.stock_quantity && data.stock_quantity > 0
                  ? (t("product_detail.in_stock") as string)
                  : (t("product_detail.out_of_stock") as string)}
              </span>
              <span className={styles.stockQuantity}>
                ({data.stock_quantity}{" "}
                {t("product_detail.units_available") as string})
              </span>
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>
            {t("product_detail.description") as string}
          </h3>
          <p className={styles.description}>
            {data.description_en ||
              (t("product_detail.no_description") as string)}
          </p>
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>
            {t("product_detail.store") as string}
          </h3>
          <p className={styles.description}>
            <strong>{t("product_detail.store_name") as string} </strong>{" "}
            {data.product?.store?.name}
          </p>
          <p className={styles.description}>
            <strong>{t("product_detail.store_contact") as string} </strong>
            {data.product?.store?.contact_phone}
          </p>
          <p className={styles.description}>
            <strong>{t("product_detail.store_email") as string} </strong>{" "}
            {data.product?.store?.email}
          </p>
          <p className={styles.description}>
            <strong>{t("product_detail.store_address") as string} </strong>
            {data.product?.store?.business_address}
          </p>
          <p className={styles.description}>
            <strong>{t("product_detail.store_percent") as string} </strong>{" "}
            {data.product?.store?.percent}%
          </p>
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>
            {t("product_detail.product_details") as string}
          </h3>
          <div className={styles.detailsGrid}>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>
                {t("product_detail.brand") as string}
              </span>
              <span className={styles.detailValue}>
                {data.product?.brand?.name}
              </span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>
                {t("product_detail.category") as string}
              </span>
              <span className={styles.detailValue}>
                {data.product?.category?.name}
              </span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>
                {t("product_detail.currency") as string}
              </span>
              <span className={styles.detailValue}>
                {data.product?.currency?.name}
              </span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>
                {t("product_detail.created") as string}
              </span>
              <span className={styles.detailValue}>
                {new Date(data.created!).toLocaleDateString("en-US")}
              </span>
            </div>
          </div>
        </div>
        {data.attributes && data.attributes.length > 0 && (
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>
              {t("product_detail.attributes") as string}
            </h3>
            <div className={styles.attributesGrid}>
              {data.attributes.map((attr) => (
                <div key={attr.attribute_id} className={styles.attributeItem}>
                  <span className={styles.attributeName}>
                    {attr.attribute_name}:
                  </span>
                  <span className={styles.attributeValue}>{attr.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
